import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, CheckCircle, CreditCard, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const statusColors = { sent: 'bg-sky-100 text-sky-700', paid: 'bg-emerald-100 text-emerald-700', overdue: 'bg-red-100 text-red-700', cancelled: 'bg-zinc-100 text-zinc-500' };

export default function CustomerPortal() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/public/invoices/${invoiceId}`)
      .then(({ data }) => setData(data))
      .catch(() => setError('Invoice not found'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  if (loading) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><div className="animate-pulse w-full max-w-md h-96 bg-zinc-200 rounded-lg" /></div>;
  if (error) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center"><p className="text-zinc-500">{error}</p></div>;

  const { invoice: inv, business } = data;

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="border border-zinc-200 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-md mb-3" style={{ backgroundColor: business?.color || '#09090b' }}>
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold font-heading" data-testid="portal-business">{business?.name}</h1>
              {business?.tagline && <p className="text-sm text-zinc-500">{business.tagline}</p>}
            </div>

            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Invoice</p>
                <p className="text-lg font-bold" data-testid="portal-invoice-number">{inv.invoice_number}</p>
              </div>
              <Badge variant="secondary" className={`text-xs ${statusColors[inv.status] || ''}`} data-testid="portal-status">{inv.status}</Badge>
            </div>

            <div className="text-sm text-zinc-600 mb-4 space-y-1">
              <p><span className="text-zinc-400">Bill to:</span> {inv.customer_name}</p>
              <p><span className="text-zinc-400">Date:</span> {new Date(inv.created_at).toLocaleDateString()}</p>
            </div>

            <div className="border border-zinc-200 rounded-lg overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-50 text-xs text-zinc-500 uppercase"><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                <tbody>{inv.line_items?.map((li, i) => (
                  <tr key={i} className="border-t border-zinc-100"><td className="p-2">{li.description}</td><td className="p-2 text-right">{li.quantity}</td><td className="p-2 text-right">${li.unit_price?.toFixed(2)}</td><td className="p-2 text-right font-semibold">${li.total?.toFixed(2)}</td></tr>
                ))}</tbody>
                <tfoot>
                  <tr className="border-t border-zinc-200"><td colSpan="3" className="p-2 text-right text-zinc-500">Subtotal</td><td className="p-2 text-right font-semibold">${inv.subtotal?.toFixed(2)}</td></tr>
                  {inv.tax_amount > 0 && <tr><td colSpan="3" className="p-2 text-right text-zinc-500">Tax ({inv.tax_rate}%)</td><td className="p-2 text-right">${inv.tax_amount?.toFixed(2)}</td></tr>}
                  <tr className="border-t bg-zinc-50"><td colSpan="3" className="p-2 text-right font-bold">Total Due</td><td className="p-2 text-right text-xl font-bold" data-testid="portal-total">${inv.total?.toFixed(2)}</td></tr>
                </tfoot>
              </table>
            </div>

            {inv.notes && <p className="text-xs text-zinc-600 mb-4">{inv.notes}</p>}

            {inv.status === 'sent' && inv.payment_link_url && (
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-base h-12" asChild data-testid="portal-pay-button">
                <a href={inv.payment_link_url} target="_blank" rel="noopener noreferrer">
                  <CreditCard className="w-5 h-5 mr-2" /> Pay Now - ${inv.total?.toFixed(2)}
                </a>
              </Button>
            )}
            {inv.status === 'sent' && !inv.payment_link_url && (
              <p className="text-sm text-zinc-500 text-center">Payment link pending. Contact us for payment options.</p>
            )}
            {inv.status === 'paid' && (
              <div className="p-4 bg-emerald-50 rounded-lg flex items-center justify-center gap-2">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                <span className="text-lg font-bold text-emerald-800">Paid - Thank You!</span>
              </div>
            )}
            {inv.status === 'overdue' && inv.payment_link_url && (
              <Button className="w-full bg-red-600 hover:bg-red-700 text-base h-12" asChild data-testid="portal-pay-overdue">
                <a href={inv.payment_link_url} target="_blank" rel="noopener noreferrer">
                  <CreditCard className="w-5 h-5 mr-2" /> Pay Overdue Invoice - ${inv.total?.toFixed(2)}
                </a>
              </Button>
            )}

            {business?.contact_phone && (
              <p className="text-xs text-zinc-400 text-center mt-4">Questions? Call {business.contact_phone}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
